import useSiteConfiguration from '@frontend/libs/hooks/useSiteConfiguration'
import {
  Image,
  Modal,
  ModalBody,
  ModalContent,
  useDisclosure,
} from '@nextui-org/react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useEffect } from 'react'
import { useCookies } from 'react-cookie'
import toast from 'react-hot-toast'

import { submitForm } from '../lib/service'
import SubscriptionPopupForm, { type FormState } from './SubscriptionPopupForm'

dayjs.extend(isBetween)

export function SubscriptionPopup() {
  const { settings } = useSiteConfiguration()
  const subscription = settings?.subscription

  const { isOpen, onClose, onOpen, onOpenChange } = useDisclosure()
  const cookieName = `subscription-popup-event-closed`
  const [cookies, setCookie] = useCookies([cookieName])

  const isClosed = cookies[cookieName] ?? false

  useEffect(() => {
    if (!isClosed) {
      const timer = setTimeout(() => {
        onOpen()
      }, 10000) // Show the popup after 10 seconds

      return () => clearTimeout(timer) // Cleanup the timer on component unmount
    }
  }, [isClosed, onOpen])

  if (subscription?.enabled === false) {
    return
  }

  if (isClosed) {
    return
  }

  function handleOnClose() {
    const expireDate = dayjs().add(3, 'day').toDate()
    setCookie(cookieName, true, { expires: expireDate, path: '/' })
    onClose()
  }

  const handleOnSubmit = async (data: FormState) => {
    await toast
      .promise(
        submitForm({
          data,
          formId: 6,
        }),
        {
          error: 'Something went wrong',
          loading: 'Submitting...',
          success: (response) => {
            return response.result.message || 'Subscribed successfully'
          },
        },
      )
      .then(() => {
        handleOnClose()
      })
  }

  return (
    <Modal
      classNames={{
        base: 'p-0',
        body: 'p-0',
        closeButton: 'rounded-none top-4 right-4 text-2xl z-20 text-black',
      }}
      isOpen={isOpen}
      onClose={handleOnClose}
      onOpenChange={onOpenChange}
      placement="center"
      size="5xl"
    >
      <ModalContent>
        <ModalBody>
          {subscription?.image && (
            <Image
              alt="ASAVA"
              className="aspect-[2/1] h-full w-full object-cover"
              removeWrapper
              src={subscription?.image?.src}
              srcSet={subscription?.image?.srcset}
            />
          )}
          <div className="p-6 md:p-14">
            <div className="mb-8 space-y-8 text-center">
              <h2 className="text-2xl font-medium uppercase">
                {subscription?.title}
              </h2>
              <p
                className="uppercase"
                dangerouslySetInnerHTML={{
                  __html: subscription?.description || '',
                }}
              />
            </div>
            <SubscriptionPopupForm onSubmit={handleOnSubmit} />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default SubscriptionPopup
